/**
 * Lindy - Drivers and Manuals Search & Filter
 * Handles search, filtering, and pagination for the drivers and manuals section
 * See sources/docs/drivers-and-manuals.md.
 */

class LindyDriversManuals {
  constructor() {
    this.searchInput = document.getElementById('lindy-drivers-manuals-search');
    this.categoryButtons = document.querySelectorAll('.lindy-category-card');
    this.rows = document.querySelectorAll('.lindy-row');
    this.paginationContainers = [
      document.getElementById('pagination-container-mobile-top'),
      document.getElementById('pagination-container-top'),
      document.getElementById('pagination-container-bottom')
    ];
    this.tableContainer = document.querySelector('.lindy-table-container');
    this.sectionContainer = document.querySelector('.lindy-drivers-manuals-section');
    this.resultsBody = document.getElementById('lindy-results');
    this.loadingNote = document.querySelector('.lindy-loading-note');
    this.searchIcon = null;
    this.loadingSpinner = null;
    this.driverCountElement = null;
    this.manualCountElement = null;
    this.emptyRow = document.querySelector('.lindy-empty-row');

    // For fetching the rest of the pages (see note at top of the .liquid file)
    this.totalPages = Number(this.sectionContainer?.dataset.totalPages) || 1;
    this.currentServerPage = Number(this.sectionContainer?.dataset.currentPage) || 1;
    this.basePath = this.sectionContainer?.dataset.basePath || window.location.pathname;
    this.shopifySectionId = this.sectionContainer?.dataset.sectionId || '';
    this.allPagesLoaded = this.totalPages <= 1;

    this.currentTab = 'drivers';
    this.currentPage = 1;
    this.rowsPerPage = 20;
    this.searchTerm = '';
    this.loadingStartTime = null;
    this.minLoadingDuration = 2000; // 2 seconds minimum for visual feedback

    // Caches for getRowSearchText()/getSearchMatches() below
    this.searchTextCache = new WeakMap();
    this.rowsVersion = 0;
    this.matchesCache = null;

    this.init();
  }

  init() {
    if (!this.searchInput || !this.categoryButtons.length) return;

    // Create loading spinner element
    this.createLoadingSpinner();

    // Get count elements
    this.categoryButtons.forEach(button => {
      const label = button.querySelector('.lindy-category-label');
      if (button.dataset.tab === 'drivers') {
        this.driverCountElement = label;
      } else if (button.dataset.tab === 'manuals') {
        this.manualCountElement = label;
      }
    });

    // Search functionality with debounce
    this.searchInput.addEventListener('input', this.debounce(() => {
      if (this.searchInput instanceof HTMLInputElement) {
        this.searchTerm = this.searchInput.value.toLowerCase();
        this.currentPage = 1;
        this.showLoadingAndFilter();
      }
    }, 300));

    // Category card switching
    this.categoryButtons.forEach(button => {
      button.addEventListener('click', () => {
        if (button instanceof HTMLElement) {
          this.currentPage = 1;
          this.activateTab(button.dataset.tab || 'drivers');
          this.filterAndPaginate();
          this.scrollToTop();
        }
      });
    });

    // Initial render (page 1 only - see constructor note)
    this.filterAndPaginate();

    // Fetch the rest of the catalog's pages in the background, if any
    this.loadRemainingPages();
  }

  /**
   * Fetch the rest of this section's pages (if any), append their rows,
   * then compute counts and the current view for the first time - the
   * category labels show no count until this has run.
   */
  async loadRemainingPages() {
    if (!this.allPagesLoaded && this.resultsBody && this.shopifySectionId) {
      if (this.loadingNote) this.loadingNote.hidden = false;

      const pageNumbers = [];
      for (let page = 1; page <= this.totalPages; page++) {
        if (page !== this.currentServerPage) pageNumbers.push(page);
      }

      const results = await Promise.all(pageNumbers.map(page => this.fetchAndAppendPage(page)));

      this.allPagesLoaded = true;
      if (this.loadingNote) {
        const anyFailed = results.includes(false);
        this.loadingNote.textContent = anyFailed
          ? 'Some results failed to load - try refreshing the page.'
          : this.loadingNote.textContent;
        this.loadingNote.hidden = !anyFailed;
      }

      // Re-query so filtering/pagination/counts see the complete set, not just page 1.
      this.rows = document.querySelectorAll('.lindy-row');
      this.rowsVersion++;
    }

    this.updateCategoryCounts();
    this.filterAndPaginate();
  }

  /**
   * Fetch a single page of this section and append its rows to the table.
   * @param {number} page
   * @returns {Promise<boolean>} Whether the page loaded successfully
   */
  async fetchAndAppendPage(page) {
    try {
      const url = `${this.basePath}?section_id=${this.shopifySectionId}&page=${page}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);

      const html = await response.text();
      const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
      const newRows = parsedDoc.querySelectorAll('.lindy-row');

      const fragment = document.createDocumentFragment();
      newRows.forEach(row => {
        // Stay hidden until loadRemainingPages() re-filters the complete set
        row.hidden = true;
        fragment.appendChild(row);
      });
      this.resultsBody.appendChild(fragment);
      return true;
    } catch (error) {
      console.error(`Failed to load drivers/manuals page ${page}:`, error);
      return false;
    }
  }

  filterAndPaginate() {
    const filteredRows = this.getFilteredRows();
    const totalPages = Math.ceil(filteredRows.length / this.rowsPerPage);

    // Hide all rows first (including empty row)
    this.rows.forEach(row => {
      if (row instanceof HTMLElement) {
        row.hidden = true;
      }
    });
    if (this.emptyRow) {
      this.emptyRow.hidden = true;
    }

    // Check if we have results
    if (filteredRows.length === 0) {
      // Show empty message
      if (this.emptyRow) {
        this.emptyRow.hidden = false;
      }
      // Clear pagination
      this.paginationContainers.forEach(container => {
        if (container) container.innerHTML = '';
      });
      return;
    }

    // Show only rows for current page
    const startIndex = (this.currentPage - 1) * this.rowsPerPage;
    const endIndex = startIndex + this.rowsPerPage;

    filteredRows.slice(startIndex, endIndex).forEach(row => {
      if (row instanceof HTMLElement) {
        row.hidden = false;
      }
    });

    // Update pagination
    this.renderPagination(totalPages, filteredRows.length);
  }

  /**
   * Get loading spinner elements (inline in search button)
   */
  createLoadingSpinner() {
    this.searchIcon = document.querySelector('.lindy-search-icon');
    this.loadingSpinner = document.querySelector('.lindy-search-spinner');
  }

  /**
   * Set the active category tab and its button styling
   * @param {string} tab - 'drivers' or 'manuals'
   */
  activateTab(tab) {
    this.currentTab = tab;
    this.categoryButtons.forEach(button => {
      button.classList.toggle('active', button.dataset.tab === tab);
    });
  }

  /**
   * Switch tabs if the search only matches the other category
   */
  switchToCategoryWithResults() {
    if (!this.searchTerm) return;

    const matches = this.getSearchMatches();
    if (matches[this.currentTab].length > 0) return;

    const otherTab = this.currentTab === 'drivers' ? 'manuals' : 'drivers';
    if (matches[otherTab].length > 0) this.activateTab(otherTab);
  }

  /**
   * Show loading spinner for minimum duration, then filter
   */
  async showLoadingAndFilter() {
    if (!this.loadingSpinner || !this.searchIcon) return;

    // Show spinner, hide search icon
    this.loadingStartTime = Date.now();
    this.searchIcon.style.display = 'none';
    this.loadingSpinner.style.display = 'block';

    // Switch tabs first if the search only matches the other category
    this.switchToCategoryWithResults();

    // Perform the filtering
    this.filterAndPaginate();

    // Update counts
    this.updateCategoryCounts();

    // Calculate remaining time to reach minimum duration
    const elapsed = Date.now() - this.loadingStartTime;
    const remaining = Math.max(0, this.minLoadingDuration - elapsed);

    // Wait for remaining time, then hide spinner and show search icon
    await new Promise(resolve => setTimeout(resolve, remaining));

    this.loadingSpinner.style.display = 'none';
    this.searchIcon.style.display = 'block';
  }

  /**
   * A row's product name + part number, lowercased, computed once and cached
   * @param {HTMLElement} row
   * @returns {string}
   */
  getRowSearchText(row) {
    let text = this.searchTextCache.get(row);
    if (text === undefined) {
      const productName = row.querySelector('.lindy-cell-product')?.textContent?.toLowerCase() || '';
      const partNumber = row.querySelector('.lindy-cell-part')?.textContent?.toLowerCase() || '';
      text = `${productName} ${partNumber}`;
      this.searchTextCache.set(row, text);
    }
    return text;
  }

  /**
   * Rows matching the search term, keyed by tab name and cached per term
   * @returns {{drivers: HTMLElement[], manuals: HTMLElement[]}}
   */
  getSearchMatches() {
    if (
      this.matchesCache &&
      this.matchesCache.rowsVersion === this.rowsVersion &&
      this.matchesCache.term === this.searchTerm
    ) {
      return this.matchesCache.matches;
    }

    const tabForType = { driver: 'drivers', manual: 'manuals' };
    const matches = { drivers: [], manuals: [] };
    this.rows.forEach(row => {
      if (!(row instanceof HTMLElement)) return;
      if (this.searchTerm && !this.getRowSearchText(row).includes(this.searchTerm)) return;
      matches[tabForType[row.dataset.type]]?.push(row);
    });

    this.matchesCache = { rowsVersion: this.rowsVersion, term: this.searchTerm, matches };
    return matches;
  }

  /**
   * Update the category counts based on the search term (not the active tab)
   */
  updateCategoryCounts() {
    const matches = this.getSearchMatches();

    if (this.driverCountElement) {
      this.driverCountElement.textContent = `Drivers (${matches.drivers.length})`;
    }
    if (this.manualCountElement) {
      this.manualCountElement.textContent = `Manuals (${matches.manuals.length})`;
    }
  }

  getFilteredRows() {
    return this.getSearchMatches()[this.currentTab] || [];
  }

  /**
   * Render pagination controls
   * @param {number} totalPages - Total number of pages
   * @param {number} totalResults - Total number of filtered results
   */
  renderPagination(totalPages, totalResults) {
    this.paginationContainers.forEach(container => {
      if (!container) return;

      container.innerHTML = '';

      if (totalPages <= 1) return;

      const isMobile = window.innerWidth <= 768;

      if (isMobile) {
        // --- MOBILE PAGINATION: Simple prev/next with page info ---
        const prevButton = this.createPaginationButton('‹', () => {
          if (this.currentPage > 1) {
            this.currentPage--;
            this.filterAndPaginate();
          }
        });
        if (this.currentPage === 1) prevButton.disabled = true;
        container.appendChild(prevButton);

        const pageInfo = document.createElement('span');
        pageInfo.className = 'lindy-pagination-page-info';
        pageInfo.textContent = `Page ${this.currentPage} of ${totalPages}`;
        container.appendChild(pageInfo);

        const nextButton = this.createPaginationButton('›', () => {
          if (this.currentPage < totalPages) {
            this.currentPage++;
            this.filterAndPaginate();
          }
        });
        if (this.currentPage === totalPages) nextButton.disabled = true;
        container.appendChild(nextButton);

      } else {
        // --- DESKTOP PAGINATION: Full numbered pagination ---

        // Previous button
        if (this.currentPage > 1) {
          const prevButton = this.createPaginationButton('‹', () => {
            this.currentPage--;
            this.filterAndPaginate();
          });
          container.appendChild(prevButton);
        }

        // Calculate visible page range
        const maxVisiblePages = 7;
        let startPage = Math.max(1, this.currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
          startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        // First page + ellipsis
        if (startPage > 1) {
          container.appendChild(this.createPaginationButton(1, () => {
            this.currentPage = 1;
            this.filterAndPaginate();
          }));

          if (startPage > 2) {
            const ellipsis = document.createElement('span');
            ellipsis.textContent = '...';
            ellipsis.className = 'lindy-pagination-ellipsis';
            container.appendChild(ellipsis);
          }
        }

        // Page numbers
        for (let i = startPage; i <= endPage; i++) {
          const button = this.createPaginationButton(i, () => {
            this.currentPage = i;
            this.filterAndPaginate();
          });

          if (i === this.currentPage) {
            button.classList.add('active');
          }

          container.appendChild(button);
        }

        // Last page + ellipsis
        if (endPage < totalPages) {
          if (endPage < totalPages - 1) {
            const ellipsis = document.createElement('span');
            ellipsis.textContent = '...';
            ellipsis.className = 'lindy-pagination-ellipsis';
            container.appendChild(ellipsis);
          }

          container.appendChild(this.createPaginationButton(totalPages, () => {
            this.currentPage = totalPages;
            this.filterAndPaginate();
          }));
        }

        // Next button
        if (this.currentPage < totalPages) {
          const nextButton = this.createPaginationButton('›', () => {
            this.currentPage++;
            this.filterAndPaginate();
          });
          container.appendChild(nextButton);
        }
      }
    });
  }

  /**
   * Scroll to top of the section
   */
  scrollToTop() {
    if (this.sectionContainer) {
      const sectionTop = this.sectionContainer.offsetTop;
      window.scrollTo({
        top: sectionTop,
        behavior: 'smooth'
      });
    }
  }

  /**
   * Create a pagination button
   * @param {string|number} text - Button text/label
   * @param {Function} onClick - Click handler
   * @returns {HTMLButtonElement}
   */
  createPaginationButton(text, onClick) {
    const button = document.createElement('button');
    button.className = 'lindy-pagination-button';
    button.textContent = String(text);
    button.addEventListener('click', /** @type {EventListener} */ ((e) => {
      onClick(e);
      this.scrollToTop();
    }));
    return button;
  }

  /**
   * Debounce helper
   * @param {Function} func - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @returns {Function}
   */
  debounce(func, wait) {
    /** @type {number|undefined} */
    let timeout;
    return /** @type {Function} */ function executedFunction(/** @type {any[]} */ ...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new LindyDriversManuals();
  });
} else {
  new LindyDriversManuals();
}
